import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react'
import api from '@/lib/axiosInstance'
import { isGalleryImage,
    AIIntakeContext,
    MAX_AUDIO_BYTES,
    MAX_FILE_BYTES,
    MAX_HELD_FILES,
    MAX_VIDEO_BYTES,
    kindOf,
    type AIIntakeValue,
    type HeldFile,
    type IntakeDraft,
    type IntakeMessage,
    type IntakeSessionDetail,
    type IntakeSessionRef,
    type IntakeSource,
} from './aiIntake'
import { pickProof } from './proofOfOwnership'

/**
 * Holds the AI listing flow's files and drafts for as long as the page lives,
 * and mirrors the drafts into a saved session on the API.
 *
 * It sits above the wizard rather than inside it, because creating one listing
 * from a batch remounts the wizard to clear every field, and the remaining
 * drafts and their photos have to survive that.
 *
 * The session is what survives a refresh: the drafts, the sources with their
 * transcripts, the conversation, and which drafts already became listings.
 * Files do not: a File object cannot be saved by the browser, so photos are
 * re-added after a reload. The page says so rather than pretending otherwise.
 */
let sequence = 0
const nextId = () => `held-${Date.now()}-${(sequence += 1)}`

export const AIIntakeProvider = ({ children }: { children: ReactNode }) => {
    const [files, setFiles] = useState<HeldFile[]>([])
    const filesRef = useRef(files)
    filesRef.current = files
    const [drafts, setDraftsState] = useState<IntakeDraft[]>([])
    // The latest drafts, for edits that land after an await (a Google lookup)
    // and must build on whatever changed in the meantime.
    const draftsRef = useRef(drafts)
    draftsRef.current = drafts
    const [sources, setSources] = useState<IntakeSource[]>([])
    const [messages, setMessages] = useState<IntakeMessage[]>([])
    const [session, setSession] = useState<IntakeSessionRef | null>(null)
    const [assignments, setAssignments] = useState<Record<number, string[]>>({})
    // Photos already offered to the listings, so one the agent took out of a
    // listing is not put back the next time the AI answers.
    const offered = useRef<Set<string>>(new Set())
    // Listings whose proof of ownership the agent removed by hand.
    const proofDismissed = useRef<Set<number>>(new Set())
    // Proof of ownership, per draft. A File cannot be saved with the
    // conversation, so it lives here until the listing is created.
    const [proofs, setProofs] = useState<Record<number, File>>({})
    const [completed, setCompleted] = useState<number[]>([])
    const [created, setCreated] = useState<{ draftIndex: number; listingId: number }[]>([])
    const [active, setActive] = useState<number | null>(null)

    /** Holds new files; given a draft, they also join that listing's gallery. */
    const addFiles = useCallback((incoming: File[], assignTo?: number) => {
        const problems: string[] = []
        const accepted: HeldFile[] = []
        for (const file of incoming) {
            const kind = kindOf(file)
            if (!kind) {
                problems.push(`${file.name} is not a PDF, Excel sheet, photo, voice note or video`)
                continue
            }
            const ceiling = kind === 'video' ? MAX_VIDEO_BYTES : kind === 'audio' ? MAX_AUDIO_BYTES : MAX_FILE_BYTES
            if (file.size > ceiling) {
                problems.push(`${file.name} is over ${ceiling / 1024 / 1024} MB`)
                continue
            }
            accepted.push({
                id: nextId(),
                file,
                kind,
                preview: kind === 'image' ? URL.createObjectURL(file) : null,
                galleryOnly: assignTo !== undefined,
            })
        }
        const room = MAX_HELD_FILES - files.length
        if (accepted.length > room) problems.push(`This page holds ${MAX_HELD_FILES} files at a time`)
        const keep = accepted.slice(0, Math.max(0, room))
        // Anything that did not fit still created an object URL.
        accepted.slice(keep.length).forEach((f) => f.preview && URL.revokeObjectURL(f.preview))
        setFiles((old) => [...old, ...keep])
        // An uploaded photo is in the gallery straight away: of the listing it
        // was added for, or of every listing when it came with the documents.
        // The agent removes the ones a listing should not have.
        const media = keep.filter((f) => f.kind === 'image' || f.kind === 'video').map((f) => f.id)
        if (media.length) {
            media.forEach((id) => offered.current.add(id))
            const targets = assignTo !== undefined ? [assignTo] : Array.from({ length: drafts.length }, (_, i) => i)
            if (targets.length) setAssignments((old) => ({ ...old, ...Object.fromEntries(targets.map((t) => [t, [...(old[t] ?? []), ...media]])) }))
        }
        return problems
    }, [files.length, drafts.length])

    const removeFile = useCallback((id: string) => {
        setFiles((old) => {
            const gone = old.find((f) => f.id === id)
            if (gone?.preview) URL.revokeObjectURL(gone.preview)
            return old.filter((f) => f.id !== id)
        })
        setAssignments((old) =>
            Object.fromEntries(Object.entries(old).map(([k, ids]) => [k, ids.filter((x) => x !== id)])),
        )
    }, [])

    const clearFiles = useCallback(() => {
        setFiles((old) => {
            old.forEach((f) => f.preview && URL.revokeObjectURL(f.preview))
            return []
        })
        setAssignments({})
        offered.current.clear()
    }, [])

    /**
     * New drafts replace the old ones. Every photo and video that was uploaded
     * is in each listing's gallery by default, including ones the AI thought
     * had nothing to do with the property: the agent chose to upload them, and
     * takes out what does not belong. A photo already taken out of a listing
     * stays out when the AI answers again.
     *
     * The strongest authoritative document among the uploads (a title, a
     * land-office search, a quit rent or assessment bill, a signed agreement)
     * is the proof of ownership, so it is attached as that without being asked
     * for a second time. One the agent took off by hand stays off.
     */
    const setDrafts = useCallback((next: IntakeDraft[], nextSources: IntakeSource[] = [], nextSession: IntakeSessionRef | null = null) => {
        const sameListings = next.length === draftsRef.current.length
        setDraftsState(next)
        setSources(nextSources)
        setCompleted(nextSession?.completed ?? [])
        setCreated(nextSession?.listings ?? [])
        if (nextSession) setSession({ publicId: nextSession.publicId, title: nextSession.title })
        setActive(null)

        const held = filesRef.current
        // A photo is in the gallery by itself; an image of a document (a title,
        // a survey plan) is evidence, and is offered as the proof instead.
        const media = held.filter((f) => f.kind === 'video' || (f.kind === 'image' && (f.galleryOnly || isGalleryImage(f.file.name, nextSources)))).map((f) => f.id)
        const fresh = media.filter((id) => !offered.current.has(id))
        media.forEach((id) => offered.current.add(id))
        setAssignments((old) =>
            Object.fromEntries(next.map((_, i) => [i, sameListings && old[i] ? [...old[i].filter((id) => media.includes(id)), ...fresh.filter((id) => !old[i].includes(id))] : media])),
        )

        if (!sameListings) proofDismissed.current.clear()
        setProofs((old) => {
            const out = { ...old }
            next.forEach((draft, i) => {
                if (out[i] || proofDismissed.current.has(i)) return
                const chosen = pickProof(nextSources, draft, next.length)
                const file = chosen ? held.find((f) => f.file.name === chosen.file) : undefined
                if (file) out[i] = file.file
            })
            return out
        })
    }, [])

    /** A saved session reopened after a refresh or from the list. */
    const hydrate = useCallback((detail: IntakeSessionDetail) => {
        clearFiles()
        setDraftsState(detail.drafts)
        setSources(detail.sources)
        setMessages(detail.messages)
        setCompleted(detail.completed)
        setCreated(detail.listings)
        setSession({ publicId: detail.publicId, title: detail.title })
        setActive(null)
        setAssignments({})
    }, [clearFiles])

    const appendMessages = useCallback((next: IntakeMessage[]) => {
        setMessages((old) => [...old, ...next])
    }, [])

    const setProof = useCallback((draftIndex: number, file: File | null) => {
        // Taken off by hand, it is not attached again by itself.
        if (file) proofDismissed.current.delete(draftIndex)
        else proofDismissed.current.add(draftIndex)
        setProofs((old) => {
            if (file) return { ...old, [draftIndex]: file }
            return Object.fromEntries(Object.entries(old).filter(([key]) => Number(key) !== draftIndex))
        })
    }, [])

    const toggleAssignment = useCallback((draftIndex: number, fileId: string) => {
        setAssignments((old) => {
            const held = old[draftIndex] ?? []
            return {
                ...old,
                [draftIndex]: held.includes(fileId) ? held.filter((x) => x !== fileId) : [...held, fileId],
            }
        })
    }, [])

    /** The API commits this record in the same transaction as listing creation. */
    const markCompleted = useCallback((draftIndex: number, listingId?: number) => {
        setCompleted((old) => old.includes(draftIndex) ? old : [...old, draftIndex])
        setCreated((old) => listingId && !old.some((x) => x.draftIndex === draftIndex) ? [...old, { draftIndex, listingId }] : old)
        setActive(null)
    }, [])

    const updateDraft = useCallback(
        (draftIndex: number, change: (draft: IntakeDraft) => IntakeDraft) => {
            const nextDrafts = draftsRef.current.map((d, i) => (i === draftIndex ? change(d) : d))
            draftsRef.current = nextDrafts
            setDraftsState(nextDrafts)
            if (session) {
                // Best effort: the edit is on this page either way, and goes to
                // the AI as part of the drafts on the next message.
                api.patch(`/listings/ai/sessions/${session.publicId}`, { drafts: nextDrafts }, { headers: { 'Content-Type': 'application/json' } }).catch(() => {})
            }
        },
        [session],
    )

    const reset = useCallback(() => {
        clearFiles()
        setDraftsState([])
        setSources([])
        setMessages([])
        setSession(null)
        setCompleted([])
        setCreated([])
        setActive(null)
    }, [clearFiles])

    const value = useMemo<AIIntakeValue>(() => {
        const mediaFor = (draftIndex: number) => {
            const ids = assignments[draftIndex] ?? []
            return ids.map((id) => files.find((f) => f.id === id)).filter((f): f is HeldFile => Boolean(f))
        }
        return {
            files,
            drafts,
            sources,
            messages,
            session,
            assignments,
            proofs,
            completed,
            created,
            active,
            addFiles,
            removeFile,
            clearFiles,
            setDrafts,
            hydrate,
            appendMessages,
            updateDraft,
            setProof,
            toggleAssignment,
            setActive,
            markCompleted,
            reset,
            mediaFor,
            imageCountFor: (draftIndex: number) => mediaFor(draftIndex).filter((f) => f.kind === 'image').length,
        }
    }, [files, drafts, sources, messages, session, assignments, proofs, completed, created, active, addFiles, removeFile, clearFiles, setDrafts, hydrate, appendMessages, updateDraft, setProof, toggleAssignment, markCompleted, reset])

    return <AIIntakeContext.Provider value={value}>{children}</AIIntakeContext.Provider>
}
